<template>
  <!--
    始终单个 slot（避免 Vue2 双 slot 错位）。
    无 catalogs 时不加 is-catalog / 目录壳 class，插槽按普通块级文档流排布，筛选条保持横排。
  -->
  <div class="catalog-sidebar-root flex1 overflow-hidden" :class="{ 'is-catalog': hasCatalog }">
    <div v-if="showTopMain" class="catalog-top-bar">
      <div class="catalog-top-scroll">
        <div
          v-for="(item, index) in mainTabs"
          :key="'main-h-' + index"
          class="catalog-top-item cursor-pointer"
          :class="[{ active: index === activeMainIndex }, 'img-' + (item.imagePosition || 'top')]"
          @click="onSelectMain(index)"
        >
          <img v-if="item.icon && item.imagePosition !== 'none'" :src="item.icon" class="cat-icon" />
          <span class="cat-name">{{ item.name }}</span>
        </div>
      </div>
    </div>

    <div :class="hasCatalog ? 'catalog-body flex flex1 overflow-hidden' : null">
      <div v-if="showLeftMain" class="catalog-sidebar-main is-main">
        <div
          v-for="(item, index) in mainTabs"
          :key="'main-v-' + index"
          class="catalog-main-item cursor-pointer"
          :class="[{ active: index === activeMainIndex }, 'img-' + (item.imagePosition || 'top')]"
          @click="onSelectMain(index)"
        >
          <img v-if="item.icon && item.imagePosition !== 'none'" :src="item.icon" class="cat-icon" />
          <span class="cat-name">{{ item.name }}</span>
        </div>
      </div>

      <div :class="hasCatalog ? 'catalog-sidebar-content flex1 flex-column overflow-hidden' : null">
        <div v-if="showTopSub" class="catalog-sub-tabs flex flex-wrap">
          <div
            v-for="(item, index) in subTabs"
            :key="'sub-h-' + index"
            class="catalog-sub-item cursor-pointer"
            :class="[{ active: index === activeSubIndex }, 'img-' + (item.imagePosition || 'top')]"
            @click="onSelectSub(index)"
          >
            <img v-if="item.icon && item.imagePosition !== 'none'" :src="item.icon" class="cat-icon" />
            <span>{{ item.name }}</span>
          </div>
        </div>

        <div :class="hasCatalog ? 'catalog-main-row flex flex1 overflow-hidden' : null">
          <div v-if="showLeftSub" class="catalog-sidebar-main is-sub">
            <div
              v-for="(item, index) in subTabs"
              :key="'sub-v-' + index"
              class="catalog-main-item cursor-pointer"
              :class="[{ active: index === activeSubIndex }, 'img-' + (item.imagePosition || 'top')]"
              @click="onSelectSub(index)"
            >
              <img v-if="item.icon && item.imagePosition !== 'none'" :src="item.icon" class="cat-icon" />
              <span class="cat-name">{{ item.name }}</span>
            </div>
          </div>

          <div :class="hasCatalog ? 'catalog-sidebar-slot flex1 flex-column overflow-hidden' : null">
            <slot></slot>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  buildMainCatalogTabs,
  buildSubCatalogTabs,
  currentMainHasSub,
  getDefaultMainCatalogIndex,
  getDefaultSubCatalogIndex
} from "@/utils/mallListSettingsView";

export default {
  name: "CatalogSidebar",
  props: {
    /** 工作副本：切主后合并 catalogDetails，并覆盖当前主目录，用于拼从目录 */
    settings: {
      type: Object,
      default: null
    },
    /** 关键字/首开整份快照：主目录固定从此构建 */
    mainSettings: {
      type: Object,
      default: null
    },
    /**
     * 当前工作副本是否来自「请求带了 mainCatalog」的 search。
     * false：未传主目录时，当前分类下级从目录读 subNodeNames
     */
    hasMainCatalog: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      activeMainIndex: 0,
      // -1：无从目录选中（第一项不是「全部」时的默认态）
      activeSubIndex: -1,
      // settings 整份刷新后按身份找回选中（不能靠旧 index 反推）
      selectedMainIdentity: "",
      selectedSubIdentity: "",
      isFirstSettingsAlign: true
    };
  },
  computed: {
    /** 主目录只用关键字快照；无从快照时回退 settings */
    mainSource() {
      return this.mainSettings || this.settings;
    },
    // 与 H5 一致：h 横向 / v 纵向 / none 无
    layout() {
      return (this.mainSource && this.mainSource.catalogLayout) || "none";
    },
    isHorizontal() {
      return this.layout === "h";
    },
    isVertical() {
      return this.layout === "v";
    },
    mainTabs() {
      return buildMainCatalogTabs(this.mainSource);
    },
    currentMain() {
      return this.mainTabs[this.activeMainIndex] || null;
    },
    /** 从目录解析上下文：是否按带 mainCatalog 的 search 结果取节点 */
    subResolveOptions() {
      return { hasMainCatalog: !!this.hasMainCatalog };
    },
    subTabs() {
      return buildSubCatalogTabs(this.settings, this.currentMain, this.subResolveOptions);
    },
    currentSub() {
      // 与高亮一致：-1 表示未选中，查询也不带具体从目录
      return this.activeSubIndex < 0 ? null : this.subTabs[this.activeSubIndex] || null;
    },
    // catalogs 为空时即便 catalogLayout 为 h/v 也不走目录壳
    hasCatalog() {
      return this.mainTabs.length > 0 && (this.isHorizontal || this.isVertical);
    },
    showTopMain() {
      return this.isHorizontal && this.mainTabs.length > 0;
    },
    showLeftMain() {
      return this.isVertical && this.mainTabs.length > 0;
    },
    showLeftSub() {
      return this.isHorizontal && currentMainHasSub(this.currentMain, this.settings) && this.subTabs.length > 0;
    },
    showTopSub() {
      return this.isVertical && currentMainHasSub(this.currentMain, this.settings) && this.subTabs.length > 0;
    }
  },
  watch: {
    /**
     * 主目录跟关键字快照；从目录跟工作副本（含 hasMainCatalog 解析方式）。
     * 不 emit：目录切换触发的 search 已发出，避免二次请求。
     * 关键字/入口重置走 resetToDefault（会 emit，由父组件 skip）。
     */
    mainSettings: {
      handler() {
        this.alignCatalogSelection();
      },
      immediate: true
    },
    settings: {
      handler() {
        this.alignCatalogSelection();
      }
    },
    hasMainCatalog() {
      this.alignCatalogSelection();
    }
  },
  methods: {
    alignCatalogSelection() {
      const mainSettings = this.mainSettings || this.settings;
      const settings = this.settings;
      const subOpts = { hasMainCatalog: !!this.hasMainCatalog };
      const mains = buildMainCatalogTabs(mainSettings);
      if (!mains.length) {
        this.activeMainIndex = 0;
        this.activeSubIndex = -1;
        this.selectedMainIdentity = "";
        this.selectedSubIdentity = "";
        // 目录被清空后，下次有 catalogs 按首包对齐，不沿用上次选中
        this.isFirstSettingsAlign = true;
        return;
      }

      if (this.isFirstSettingsAlign) {
        // 首包：优先 selected=true，无标记选第一个主目录
        this.isFirstSettingsAlign = false;
        this.activeMainIndex = getDefaultMainCatalogIndex(mains);
        this.activeSubIndex = this.computeDefaultSubIndex(
          buildSubCatalogTabs(settings, mains[this.activeMainIndex], subOpts)
        );
        this.rememberSelection();
        return;
      }

      const mainIdx = this.findTabIndex(mains, this.selectedMainIdentity);
      this.activeMainIndex = mainIdx >= 0 ? mainIdx : 0;
      const subs = buildSubCatalogTabs(settings, mains[this.activeMainIndex], subOpts);
      const subIdx = this.findTabIndex(subs, this.selectedSubIdentity);
      // 主目录变了或从目录节点失效：按「全部」规则重新默认
      this.activeSubIndex = mainIdx >= 0 && subIdx >= 0 ? subIdx : this.computeDefaultSubIndex(subs);
      this.rememberSelection();
    },
    tabIdentity(tab) {
      if (!tab) return "";
      if (tab.isAll) return "__all__";
      return `${tab.type}|${(tab.catalog && tab.catalog.name) || ""}|${tab.value || tab.filterValue || tab.name}`;
    },
    findTabIndex(tabs, identity) {
      if (!identity || !Array.isArray(tabs)) return -1;
      return tabs.findIndex(tab => this.tabIdentity(tab) === identity);
    },
    rememberSelection() {
      this.selectedMainIdentity = this.tabIdentity(this.currentMain);
      this.selectedSubIdentity = this.tabIdentity(this.currentSub);
    },
    /**
     * @param {"main"|"sub"} changeType
     * main=切主目录（需刷新从目录详情）；sub=切从目录（只搜商品，不回写 listSettings）
     */
    emitChange(changeType) {
      this.$emit("change", { main: this.currentMain, sub: this.currentSub, changeType: changeType || "main" });
    },
    /**
     * 从目录默认高亮（不限入口）：
     * - 有具体节点 selected=true → 选该项
     * - 否则：第一项是「全部」→ 选中；第一项是具体节点或无从目录 → 不选中（-1）
     */
    computeDefaultSubIndex(subs) {
      return getDefaultSubCatalogIndex(Array.isArray(subs) ? subs : this.subTabs);
    },
    /**
     * 用户切主目录：从目录回到「全部 / 不选」。
     * 不用 subNodeNames 上残留的 selected（那是上一份 search 的从目录，不属于新主目录）。
     */
    getResetSubIndexOnMainChange(subs) {
      const list = Array.isArray(subs) ? subs : this.subTabs;
      return list[0] && list[0].isAll ? 0 : -1;
    },
    /**
     * 关键字/入口查询：归到 selected=true 或第一个主目录。
     * emitChange=false：只对齐高亮，不 emit（首包/入口已 search，避免二次请求）
     */
    resetToDefault(emitChange) {
      this.activeMainIndex = getDefaultMainCatalogIndex(this.mainTabs);
      this.activeSubIndex = this.computeDefaultSubIndex();
      this.rememberSelection();
      if (emitChange !== false) {
        this.emitChange("main");
      }
    },
    /** VIN 有主目录：高亮 selected=true，否则第一个真实主目录（跳过「全部」），不 emit */
    selectFirstMainCatalog() {
      const selectedIdx = getDefaultMainCatalogIndex(this.mainTabs);
      const selectedTab = this.mainTabs[selectedIdx];
      const fallbackIdx = this.mainTabs.findIndex(tab => tab && !tab.isAll);
      this.isFirstSettingsAlign = false;
      this.activeMainIndex = selectedTab && !selectedTab.isAll ? selectedIdx : fallbackIdx >= 0 ? fallbackIdx : 0;
      this.activeSubIndex = this.computeDefaultSubIndex();
      this.rememberSelection();
    },
    /** VIN 关键字重搜：主目录冻结，只把从目录归零到「全部/不选中」 */
    resetSubToDefault() {
      this.activeSubIndex = this.computeDefaultSubIndex();
      this.rememberSelection();
      this.emitChange("sub");
    },
    onSelectMain(index) {
      this.activeMainIndex = index;
      this.activeSubIndex = this.getResetSubIndexOnMainChange();
      this.rememberSelection();
      this.emitChange("main");
    },
    onSelectSub(index) {
      this.activeSubIndex = index;
      this.rememberSelection();
      this.emitChange("sub");
    }
  }
};
</script>

<style lang="scss" scoped>
.catalog-sidebar-root {
  width: 100%;
  min-width: 0;

  &.is-catalog {
    display: flex;
    flex-direction: column;
  }
}

.catalog-top-bar {
  flex-shrink: 0;
  background: #fff;
  border-bottom: 1px solid #ebeef5;
  min-height: 44px;

  .catalog-top-scroll {
    display: flex;
    align-items: center;
    overflow-x: auto;
    padding: 0 4px;
    height: 44px;
    box-sizing: border-box;

    &::-webkit-scrollbar {
      display: none;
      height: 0;
    }
  }

  .catalog-top-item {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    height: 44px;
    padding: 0 12px;
    font-size: 13px;
    color: #606266;
    position: relative;
    transition: all 0.2s ease;

    // imagePosition 方位：right 图标在名称右侧，top/bottom 纵向排列
    &.img-right {
      flex-direction: row-reverse;
    }

    &.img-top,
    &.img-bottom {
      flex-direction: column;
    }

    &.img-bottom {
      flex-direction: column-reverse;
    }

    .cat-icon {
      width: 20px;
      height: 20px;
      border-radius: 3px;
      object-fit: cover;
    }

    &:hover {
      color: var(--color-primary);
    }

    &.active {
      color: var(--color-primary);
      font-weight: 600;

      &::after {
        content: "";
        position: absolute;
        left: 0;
        bottom: 0;
        width: 100%;
        height: 3px;
        background: var(--color-primary);
        border-radius: 2px 2px 0 0;
      }
    }
  }
}

.catalog-body {
  width: 100%;
  min-width: 0;
  min-height: 0;
}

.catalog-main-row {
  width: 100%;
  min-width: 0;
  min-height: 0;
}

.catalog-sidebar-slot {
  width: 100%;
  min-width: 0;
  min-height: 0;
}

.catalog-sidebar-main {
  width: 110px;
  flex-shrink: 0;
  background: #f5f7fa;
  overflow-y: auto;

  &.is-sub {
    width: 100px;
  }

  .catalog-main-item {
    padding: 14px 10px;
    text-align: center;
    font-size: 13px;
    color: #606266;
    border-bottom: 1px solid #ebeef5;
    transition: all 0.3s ease;
    position: relative;

    .cat-icon {
      width: 28px;
      height: 28px;
      border-radius: 4px;
      object-fit: cover;
      margin: 0 auto 4px;
      display: block;
    }

    .cat-name {
      display: block;
      word-break: break-all;
      line-height: 1.4;
    }

    // imagePosition 为 left/right 时改为横向排列，bottom 时图标在文字下方
    &.img-left,
    &.img-right {
      display: flex;
      align-items: center;
      gap: 6px;
      text-align: left;

      .cat-icon {
        margin: 0;
        flex-shrink: 0;
      }
    }

    &.img-right {
      flex-direction: row-reverse;
    }

    &.img-bottom {
      display: flex;
      flex-direction: column-reverse;
      align-items: center;

      .cat-icon {
        margin: 4px auto 0;
      }
    }

    &:hover {
      color: var(--color-primary);
      background: #eef4ff;
    }

    &.active {
      background: #fff;
      color: var(--color-primary);
      font-weight: 600;

      &::before {
        content: "";
        position: absolute;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
        width: 3px;
        height: 20px;
        background: var(--color-primary);
        border-radius: 0 2px 2px 0;
      }
    }
  }
}

.catalog-sidebar-content {
  background: #fff;
  min-width: 0;
  width: 100%;

  .catalog-sub-tabs {
    flex-shrink: 0;
    padding: 6px 12px;
    border-bottom: 1px solid #ebeef5;
    gap: 4px;

    .catalog-sub-item {
      padding: 6px 12px;
      border-radius: 4px;
      font-size: 13px;
      color: #606266;
      display: flex;
      align-items: center;
      gap: 4px;
      transition: all 0.3s ease;

      // subImagePosition 方位：right 图标在名称右侧，top/bottom 纵向排列
      &.img-right {
        flex-direction: row-reverse;
      }

      &.img-top,
      &.img-bottom {
        flex-direction: column;
      }

      &.img-bottom {
        flex-direction: column-reverse;
      }

      .cat-icon {
        width: 18px;
        height: 18px;
        border-radius: 3px;
        object-fit: cover;
      }

      &:hover {
        background: #f5f7fa;
        color: var(--color-primary);
      }

      &.active {
        background: color-mix(in srgb, var(--color-light80) 30%, transparent) !important;
        color: var(--color-primary);
        font-weight: 500;
      }
    }
  }
}
</style>
